import React, { type ReactElement } from 'react';
import { toInteger } from 'lodash';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from 'atom-components/error-message-from-hook-form';
import SingleComboBox, {
  type ComboBoxItem
} from 'atom-components/form-fields-for-hook-form/single-combo-box';
import Label from 'atom-components/label';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import { orgUsersWhoCanReviewEscalatedMarks } from './active-reviewer.helpers';
import styles from './active-reviewer.module.scss';

type Props = {
  organisationUsers: BaseCustomerUser[];
};

export default function ActiveReviewer({ organisationUsers }: Props): ReactElement {
  const { control, setValue, formState, watch } = useFormContext();
  const { errors } = formState;

  const furtherEscalated = watch('furtherEscalated');
  const filteredOrganisationUsers = orgUsersWhoCanReviewEscalatedMarks(organisationUsers);

  // Filter the list of organisation users to those with site admin/admin roles if the user has selected 'Further Escalated'.
  const reviewers = furtherEscalated ? filteredOrganisationUsers : organisationUsers;

  const selectableOrganisationUsers: ComboBoxItem[] = reviewers.map((organisationUser) => ({
    id: organisationUser.id,
    name: `${organisationUser?.user.firstname} ${organisationUser?.user.lastname}`
  }));

  const fieldName = 'organisationUserId';

  return (
    <div className={styles.activeReviewerContainer}>
      <Label htmlFor={fieldName}>Active Reviewer</Label>
      <SingleComboBox
        id={fieldName}
        name={fieldName}
        control={control}
        allItems={selectableOrganisationUsers}
        clearable
        onChange={(item): void => {
          const organisationUserId = item?.id ? toInteger(item?.id) : null;
          setValue(fieldName, organisationUserId, {
            shouldValidate: true,
            shouldTouch: true
          });
        }}
      />
      {errors.organisationUserId && <ErrorMessage errors={errors} name={fieldName} />}
    </div>
  );
}
